import { Badge, Box, Button, Card, CardBody, Flex, HStack, Progress, Text } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { Link as RouterLink } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { formatDistanceStrict } from 'date-fns';
import { UnitGenerationPhase, UnitGenerationRunStatus } from '@duke-hq/svc-api-db/client';

import { getStatusDisplay, reviewGate } from '../statusConfig';
import { testIds } from './testIds';

type RunItem = {
  id: string;
  unitCode: string;
  unitTitle: string | null;
  status: UnitGenerationRunStatus;
  phase: UnitGenerationPhase | null;
  progress: number;
  message: string | null;
  error: string | null;
  startedAt: Date;
  completedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  unitOfCompetencyId: string | null;
  unitOfCompetency: { id: string; code: string; title: string } | null;
};

export function RunCard({
  run,
  onViewDetails,
  onReviewAllocation,
}: {
  run: RunItem;
  onViewDetails: () => void;
  onReviewAllocation: () => void;
}) {
  const { unitCode, status, progress, unitOfCompetencyId } = run;
  const isRunning = status === UnitGenerationRunStatus.RUNNING;
  const isWaiting = status === UnitGenerationRunStatus.WAITING;
  const isImported = status === UnitGenerationRunStatus.IMPORTED;
  const gate = reviewGate(run);
  const awaitingAssessmentReview = isWaiting && gate === 'assessment';

  const statusDisplay = getStatusDisplay(status, run.error, gate);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!isRunning) {
      return;
    }

    const interval = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(interval);
  }, [isRunning]);

  const start = new Date(run.startedAt);
  const end = run.completedAt != null ? new Date(run.completedAt) : now;
  const diff = end.getTime() - start.getTime();
  const duration = diff < 60_000 ? '< 1 minute' : formatDistanceStrict(start, end);

  return (
    <Card
      variant="outline"
      border="1px solid"
      borderColor="border.default"
      borderRadius="md"
      w="100%"
    >
      <CardBody py={4} px={5}>
        <Flex align="center" justifyContent="space-between" gap={4}>
          <Flex align="center" gap={2} minW={0}>
            <Text fontSize="xs" fontFamily="mono" fontWeight="semibold" flexShrink={0}>
              {unitCode}
            </Text>
            {run.unitTitle != null ? (
              <Text fontSize="sm" color="fg.muted" noOfLines={1}>
                {run.unitTitle}
              </Text>
            ) : null}
          </Flex>

          <Badge
            colorScheme={statusDisplay.colorScheme}
            display="inline-flex"
            alignItems="center"
            gap={1}
            flexShrink={0}
          >
            {statusDisplay.icon}
            {statusDisplay.label}
          </Badge>
        </Flex>

        {isRunning ? (
          <Flex align="center" gap={2} mt={2}>
            <Box flex={1}>
              <Progress
                value={progress}
                size="sm"
                colorScheme="blue"
                backgroundColor="gray.300"
                borderRadius="md"
              />
            </Box>
            <Text fontSize="xs" fontWeight="medium">
              {progress}%
            </Text>
          </Flex>
        ) : null}

        {run.message != null && (isRunning || awaitingAssessmentReview) ? (
          <Text
            fontSize={awaitingAssessmentReview ? 'sm' : 'xs'}
            fontWeight={awaitingAssessmentReview ? 'medium' : 'normal'}
            color={awaitingAssessmentReview ? 'warning.emphasis' : 'fg.subtle'}
            mt={1}
            data-testid={testIds.runMessage}
          >
            {run.message}
          </Text>
        ) : null}

        <Flex mt={3} align="center" justify="space-between">
          <Text fontSize="xs" color="fg.subtle">
            {run.completedAt != null ? (
              <Trans>Completed: {new Date(run.completedAt).toLocaleString()}</Trans>
            ) : isWaiting ? (
              <Trans>Waiting since: {new Date(run.updatedAt).toLocaleString()}</Trans>
            ) : (
              <Trans>Running for: {duration}</Trans>
            )}
          </Text>
          <HStack spacing={2}>
            <Button variant="outline" size="sm" onClick={onViewDetails}>
              <Trans>View details</Trans>
            </Button>
            {gate === 'allocation' ? (
              <Button size="sm" colorScheme="brand" onClick={onReviewAllocation}>
                <Trans>Review allocation</Trans>
              </Button>
            ) : null}
            {gate === 'assessment' ? (
              <Button
                {...(unitOfCompetencyId == null
                  ? { onClick: onViewDetails }
                  : { as: RouterLink, to: `/units/${unitOfCompetencyId}/assessment` })}
                size="sm"
                colorScheme="brand"
              >
                <Trans>Review assessments</Trans>
              </Button>
            ) : null}
            {isImported && unitOfCompetencyId != null ? (
              <Button
                as={RouterLink}
                to={`/units/${unitOfCompetencyId}`}
                size="sm"
                colorScheme="brand"
              >
                <Trans>View Unit</Trans>
              </Button>
            ) : null}
          </HStack>
        </Flex>
      </CardBody>
    </Card>
  );
}
